import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { apiRequest } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import './technician.css';

type DashboardWorkOrder = {
    _id: string;
    title?: string;
    client?: { name?: string; address?: string };
    schedule?: { date?: string; startTime?: string };
    status?: string;
    priority?: 'low' | 'medium' | 'high';
};

type ClockLocation = {
    lat?: number;
    lng?: number;
    accuracy?: number;
    label?: string;
};

type TimeEntry = {
    _id: string;
    date: string;
    hours: number;
    kind?: 'manual' | 'clock';
    clockInAt?: string | null;
    clockOutAt?: string | null;
};

/** Reads geolocation. Resolves with `null` if denied/unsupported/timed-out (never throws). */
function tryGetLocation(): Promise<ClockLocation | null> {
    return new Promise((resolve) => {
        if (typeof window === 'undefined' || !('geolocation' in navigator)) {
            resolve(null);
            return;
        }
        try {
            navigator.geolocation.getCurrentPosition(
                (pos) =>
                    resolve({
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                        accuracy: pos.coords.accuracy
                    }),
                () => resolve(null),
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 30_000 }
            );
        } catch {
            resolve(null);
        }
    });
}

type InspectionRow = {
    _id: string;
    siteAddress?: string;
    status?: 'in_progress' | 'completed';
    createdAt?: string;
};
type AssignedWorkOrderRow = DashboardWorkOrder & {
    formTemplateId?: string;
    reportSummary?: { submittedCount?: number };
};

function startOfWeek(): Date {
    const d = new Date();
    const day = d.getDay(); // 0 = Sun
    const diff = day === 0 ? 6 : day - 1; // Monday-anchored
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - diff);
    return d;
}

function isScheduledToday(scheduleDate?: string): boolean {
    if (!scheduleDate) return false;
    const d = new Date(scheduleDate);
    if (Number.isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
}

function scheduleStartSort(a: DashboardWorkOrder, b: DashboardWorkOrder): number {
    const ta = a.schedule?.startTime || '99:99';
    const tb = b.schedule?.startTime || '99:99';
    return ta.localeCompare(tb);
}
function formatElapsedShort(ms: number): string {
    if (!Number.isFinite(ms) || ms < 0) ms = 0;
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h}h ${String(m).padStart(2, '0')}m`;
}

function TechDash(): React.JSX.Element {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [workOrders, setWorkOrders] = useState<DashboardWorkOrder[]>([]);
    const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null);
    const [weekEntries, setWeekEntries] = useState<TimeEntry[]>([]);
    const [inspections, setInspections] = useState<InspectionRow[]>([]);
    const [assignedJobs, setAssignedJobs] = useState<AssignedWorkOrderRow[]>([]);
    const [now, setNow] = useState<number>(Date.now());
    const [punching, setPunching] = useState(false);
    const [clockError, setClockError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const [wos, entries, active, insp, assigned] = await Promise.all([
                apiRequest<{ data: DashboardWorkOrder[] }>('/api/work-orders').catch(() => ({ data: [] })),
                apiRequest<{ data: TimeEntry[] }>('/api/time-entries').catch(() => ({ data: [] })),
                apiRequest<{ data: TimeEntry | null }>('/api/time-entries/active').catch(() => ({ data: null })),
                apiRequest<{ data: InspectionRow[] }>('/api/inspections?status=in_progress').catch(() => ({ data: [] })),
                apiRequest<{ data: AssignedWorkOrderRow[] }>('/api/work-orders?allAssigned=1').catch(() => ({ data: [] }))
            ]);
            setWorkOrders(wos.data || []);
            setWeekEntries(entries.data || []);
            setActiveEntry(active.data || null);
            setInspections(insp.data || []);
            setAssignedJobs(assigned.data || []);
        } catch {
            // load is best-effort — individual catches above handle most errors
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        if (!activeEntry?.clockInAt) return;
        const t = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(t);
    }, [activeEntry?.clockInAt]);

    const weekHours = useMemo(() => {
        const since = startOfWeek().getTime();
        return weekEntries
            .filter((e) => new Date(e.date || 0).getTime() >= since)
            .reduce((sum, e) => sum + (Number(e.hours) || 0), 0);
    }, [weekEntries]);

    const todaysRoute = useMemo(
        () =>
            workOrders
                .filter((w) => isScheduledToday(w.schedule?.date))
                .sort(scheduleStartSort),
        [workOrders]
    );

    const openInspections = useMemo(
        () => inspections.filter((i) => i.status === 'in_progress').slice(0, 5),
        [inspections]
    );

    const pendingForms = useMemo(
        () =>
            assignedJobs.filter(
                (wo) => Boolean(wo.formTemplateId) && !(wo.reportSummary?.submittedCount || 0)
            ).slice(0, 5),
        [assignedJobs]
    );

    const activeMs = activeEntry?.clockInAt
        ? now - new Date(activeEntry.clockInAt).getTime()
        : 0;

    async function handleClockPress() {
        if (punching) return;
        setClockError(null);
        setPunching(true);
        try {
            const location = await tryGetLocation();
            if (activeEntry?.clockInAt) {
                await apiRequest('/api/time-entries/clock-out', { method: 'POST', json: { location } });
            } else {
                await apiRequest('/api/time-entries/clock-in', { method: 'POST', json: { location } });
            }
            await load();
        } catch (e: unknown) {
            setClockError(e instanceof Error ? e.message : 'Clock action failed. Please try again.');
        } finally {
            setPunching(false);
        }
    }

    return (
        <AppShell>
            <div className="tech-page">
                <header className="tech-hero">
                    <div>
                        <p className="tech-kicker">Field Operations</p>
                        <h1 className="tech-title">Hi {user?.name?.split(' ')[0] || 'there'} 👋</h1>
                        <p className="tech-sub">
                            Your jobs, active checklists, and time sheet at a glance.
                        </p>
                    </div>
                    <div className="tech-actions">
                        <button
                            type="button"
                            className={`tech-btn ${activeEntry?.clockInAt ? 'tech-btn-danger' : 'tech-btn-primary'}`}
                            onClick={handleClockPress}
                            disabled={punching}
                        >
                            {punching
                                ? 'Working…'
                                : activeEntry?.clockInAt
                                    ? 'Clock out'
                                    : 'Clock in'}
                        </button>
                        <Link to="/technician/JobList" className="tech-btn tech-btn-ghost">
                            Job list
                        </Link>
                    </div>
                </header>

                {clockError ? (
                    <div className="tech-banner tech-banner-error" role="alert" style={{ marginBottom: 12 }}>
                        {clockError}
                        <button
                            type="button"
                            className="tech-banner-close"
                            onClick={() => setClockError(null)}
                            aria-label="Dismiss error"
                        >
                            ×
                        </button>
                    </div>
                ) : null}

                <section className="tech-report-grid">
                    <article className="tech-card tech-report-stat">
                        <span className="tech-report-stat-label">Clock</span>
                        <span className="tech-report-stat-value">
                            {activeEntry?.clockInAt ? formatElapsedShort(activeMs) : 'Off'}
                        </span>
                        <span className="tech-report-stat-hint">
                            {activeEntry?.clockInAt
                                ? `Since ${new Date(activeEntry.clockInAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                                : 'You are not currently clocked in.'}
                        </span>
                    </article>
                    <article className="tech-card tech-report-stat">
                        <span className="tech-report-stat-label">This week</span>
                        <span className="tech-report-stat-value">{weekHours.toFixed(1)}h</span>
                        <span className="tech-report-stat-hint">Hours logged since Monday.</span>
                    </article>
                    <article className="tech-card tech-report-stat">
                        <span className="tech-report-stat-label">Open inspections</span>
                        <span className="tech-report-stat-value">{openInspections.length}</span>
                        <span className="tech-report-stat-hint">
                            {openInspections.length === 0 ? 'Nothing waiting on you.' : 'Tap below to resume.'}
                        </span>
                    </article>
                    <article className="tech-card tech-report-stat">
                        <span className="tech-report-stat-label">Pending job forms</span>
                        <span className="tech-report-stat-value">{pendingForms.length}</span>
                        <span className="tech-report-stat-hint">Assigned work orders with unsubmitted forms.</span>
                    </article>
                </section>

                <section className="tech-card tech-report-section" style={{ marginBottom: 18 }}>
                    <div className="tech-report-section-head">
                        <h3>Today&apos;s route</h3>
                        <div className="tech-report-section-actions">
                            <Link to="/technician/JobList" className="tech-btn tech-btn-ghost">
                                See all
                            </Link>
                        </div>
                    </div>
                    <div className="tech-report-section-body">
                        {todaysRoute.length === 0 ? (
                            <p className="tech-report-empty">No jobs scheduled for today.</p>
                        ) : (
                            <ul className="tech-report-list">
                                {todaysRoute.map((order) => (
                                    <li key={order._id} className="tech-report-list-row">
                                        <div className="tech-report-row-main">
                                            <div className="tech-report-row-title">
                                                {order.client?.name || order.title || 'Assigned site'}
                                            </div>
                                            <div className="tech-report-row-meta">
                                                {order.client?.address || '—'}
                                            </div>
                                        </div>
                                        <div className="tech-report-row-right">
                                            {order.schedule?.startTime ? (
                                                <span className="tech-report-row-right-strong">
                                                    {order.schedule.startTime}
                                                </span>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="tech-btn tech-btn-primary"
                                                onClick={() => navigate(`/technician/jobs/${order._id}`)}
                                                style={{ minHeight: 36, padding: '6px 14px', fontSize: 13 }}
                                            >
                                                Open
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>

                <section className="tech-card tech-report-section" style={{ marginBottom: 18 }}>
                    <div className="tech-report-section-head">
                        <h3>Open inspections</h3>
                        <div className="tech-report-section-actions">
                            <Link to="/technician/JobList" className="tech-btn tech-btn-ghost">
                                Job list
                            </Link>
                        </div>
                    </div>
                    <div className="tech-report-section-body">
                        {openInspections.length === 0 ? (
                            <p className="tech-report-empty">No inspections in progress.</p>
                        ) : (
                            <ul className="tech-report-list">
                                {openInspections.map((insp) => (
                                    <li key={insp._id} className="tech-report-list-row">
                                        <div className="tech-report-row-main">
                                            <div className="tech-report-row-title">
                                                {insp.siteAddress || `Inspection …${insp._id.slice(-6)}`}
                                            </div>
                                            <div className="tech-report-row-meta">
                                                Created{' '}
                                                {insp.createdAt
                                                    ? new Date(insp.createdAt).toLocaleDateString()
                                                    : '—'}
                                            </div>
                                        </div>
                                        <div className="tech-report-row-right">
                                            <span className="tech-badge warn">in progress</span>
                                            <Link
                                                to={`/technician/inspections/${insp._id}/checklist`}
                                                className="tech-btn tech-btn-primary"
                                                style={{ minHeight: 36, padding: '6px 14px', fontSize: 13 }}
                                            >
                                                Resume
                                            </Link>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>

                <section className="tech-card tech-report-section">
                    <div className="tech-report-section-head">
                        <h3>Pending job forms</h3>
                        <div className="tech-report-section-actions">
                            <Link to="/technician/JobLog" className="tech-btn tech-btn-ghost">
                                Job log
                            </Link>
                        </div>
                    </div>
                    <div className="tech-report-section-body">
                        {pendingForms.length === 0 ? (
                            <p className="tech-report-empty">
                                All assigned work-order forms are submitted for now.
                            </p>
                        ) : (
                            <ul className="tech-report-list">
                                {pendingForms.map((wo) => (
                                    <li key={wo._id} className="tech-report-list-row">
                                        <div className="tech-report-row-main">
                                            <div className="tech-report-row-title">
                                                {wo.title || wo.client?.name || 'Assigned job'}
                                            </div>
                                            <div className="tech-report-row-meta">
                                                {wo.client?.address || '—'}
                                            </div>
                                        </div>
                                        <div className="tech-report-row-right">
                                            <span className="tech-badge warn">Not submitted</span>
                                            <button
                                                type="button"
                                                className="tech-btn tech-btn-primary"
                                                onClick={() => navigate(`/technician/jobs/${wo._id}`)}
                                                style={{ minHeight: 36, padding: '6px 14px', fontSize: 13 }}
                                            >
                                                Open
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>
            </div>
        </AppShell>
    );
}

export default TechDash;
